import { useState } from 'react';

const FAQS = [
  {
    q: 'When will the beta launch?',
    a: "We're targeting a beta launch in Q3 2025. Waitlist members will receive exclusive early access invites in the order they signed up — with referrals bumping you higher. You'll get an email the moment your spot opens.",
    icon: 'ri-calendar-event-line',
  },
  {
    q: 'Is WingmanFix really free?',
    a: 'Yes — 100% free during the entire beta period. No credit card required, no hidden charges. After beta, we plan to offer a generous free tier alongside paid plans. Early waitlist members will receive a special founding-member discount.',
    icon: 'ri-gift-line',
  },
  {
    q: 'Do I need special hardware or equipment?',
    a: 'No special hardware needed. WingmanFix works with your existing smartphone. For advanced features like ADS-B data and cockpit audio transcription, optional compatible accessories can be used — but they are never required.',
    icon: 'ri-tools-line',
  },
  {
    q: 'How does the referral system work?',
    a: 'After signing up, you receive a unique referral link. Every pilot who joins through your link moves you up the waitlist. Reach milestones (1, 3, 5, or 10 referrals) to unlock rewards like spot skips, a Beta badge, Founding Member status, and a lifetime discount.',
    icon: 'ri-links-line',
  },
  {
    q: 'Is my flight data private and secure?',
    a: 'Absolutely. Your flight data is encrypted in transit and at rest. We never sell your data to third parties. You own your data and can delete it at any time. WingmanFix is built with aviation-grade privacy standards from day one.',
    icon: 'ri-shield-check-line',
  },
  {
    q: 'What pilot levels is WingmanFix designed for?',
    a: 'WingmanFix is built for all levels — from student pilots logging their first hours to seasoned CFIs and ATP-rated professionals. The AI adapts its coaching and analysis to your experience level and training goals.',
    icon: 'ri-user-star-line',
  },
  {
    q: 'Can I cancel or leave the waitlist?',
    a: "Of course. You can opt out at any time by clicking 'unsubscribe' in any email we send you. No questions asked, no hard feelings — blue skies either way.",
    icon: 'ri-logout-box-line',
  },
];

export default function WaitlistFAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggle = (i: number) => setOpenIndex(openIndex === i ? null : i);

  return (
    <section className="pb-28 px-6">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <p className="text-xs font-bold text-[#00FFD1] uppercase tracking-widest mb-3">Got Questions?</p>
          <h2 className="text-3xl font-bold mb-3">Frequently Asked Questions</h2>
          <p className="text-gray-400 text-sm max-w-md mx-auto">
            Everything you need to know before joining the WingmanFix waitlist.
          </p>
        </div>

        {/* Accordion */}
        <div className="space-y-3">
          {FAQS.map((faq, i) => {
            const isOpen = openIndex === i;
            return (
              <div
                key={i}
                className={`border rounded-2xl overflow-hidden transition-all duration-300 ${
                  isOpen
                    ? 'border-[#00FFD1]/40 bg-white/5'
                    : 'border-white/10 bg-white/3 hover:border-white/20'
                }`}
              >
                <button
                  onClick={() => toggle(i)}
                  className="w-full flex items-center gap-4 px-6 py-5 text-left cursor-pointer group"
                >
                  {/* Icon */}
                  <div
                    className={`w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0 transition-all duration-300 ${
                      isOpen
                        ? 'bg-[#00FFD1]/15 border border-[#00FFD1]/40'
                        : 'bg-white/5 border border-white/10 group-hover:border-[#00FFD1]/30'
                    }`}
                  >
                    <i
                      className={`${faq.icon} text-base transition-colors duration-300 ${
                        isOpen ? 'text-[#00FFD1]' : 'text-gray-400 group-hover:text-[#00FFD1]/70'
                      }`}
                    />
                  </div>

                  {/* Question */}
                  <span
                    className={`flex-1 text-sm font-semibold transition-colors duration-300 ${
                      isOpen ? 'text-white' : 'text-gray-300 group-hover:text-white'
                    }`}
                  >
                    {faq.q}
                  </span>

                  {/* Chevron */}
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                    <i
                      className={`ri-arrow-down-s-line text-lg transition-all duration-300 ${
                        isOpen ? 'rotate-180 text-[#00FFD1]' : 'text-gray-500'
                      }`}
                    />
                  </div>
                </button>

                {/* Answer */}
                <div
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    isOpen ? 'max-h-48 opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="px-6 pb-6 pl-[4.25rem]">
                    <p className="text-gray-400 text-sm leading-relaxed">{faq.a}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

      </div>
    </section>
  );
}
